import { CONSUMER_POLICY_FILTERS_ENABLED } from './consumer-feature-flags';

/**
 * Target type the Consumer Book passes to the shared filter preset selector.
 */
export const CONSUMER_PRESET_TARGET_TYPE = 'CONSUMER';

/**
 * A built-in Consumer Book preset. `filterData` is the same shape the selector
 * saves and `ConsumerFilter.loadFromObject` reads back, so a default preset and
 * a user-saved one apply identically.
 */
export interface ConsumerDefaultPreset {
  id: string;
  name: string;
  description: string;
  filterData: Record<string, unknown>;
}

/**
 * Presets whose filters read the policy projection. They are only offered when
 * the policy filters are enabled, so flipping the flag back off cannot leave a
 * chip that applies filters the menu no longer shows.
 *
 * Neither touches plan type or carrier: those columns are empty on backfilled
 * rows, so a preset built on them would silently return nothing.
 */
const POLICY_PRESETS: ConsumerDefaultPreset[] = [
  {
    id: 'consumer-default-active-medicare',
    name: 'Active Medicare',
    description: 'Customers with a Medicare policy that is currently active.',
    filterData: {
      hasBaseCategory: [{ id: 'BC_MEDICARE', label: 'Medicare' }],
      coverageStatus: [{ id: 'active', label: 'Active coverage' }],
    },
  },
  {
    id: 'consumer-default-no-policies',
    name: 'No policies yet',
    description: 'Customers in the book who have never been written.',
    filterData: {
      policyPresence: [{ id: 'none', label: 'No policies' }],
    },
  },
];

/**
 * Presets that read customer fields only, so they work whether or not the
 * policy filters are enabled.
 */
const CONSUMER_PRESETS: ConsumerDefaultPreset[] = [
  {
    id: 'consumer-default-turning-65',
    name: 'Turning 65',
    description: 'Customers aged 64 to 65, aging into Medicare.',
    filterData: {
      age: { min: 64, max: 65 },
    },
  },
];

/**
 * Built-in presets offered on the Consumer Book before anyone saves their own.
 */
export function getConsumerDefaultPresets(): ConsumerDefaultPreset[] {
  return [
    ...(CONSUMER_POLICY_FILTERS_ENABLED ? POLICY_PRESETS : []),
    ...CONSUMER_PRESETS,
  ];
}
